import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Card, Button, Badge, Modal, Form, InputGroup, FormControl } from "react-bootstrap";
import { FaEye, FaPlus, FaTimes, FaSearch, FaCheckCircle, FaEdit, FaBuilding, FaUser } from "react-icons/fa";
import DatePicker from "react-datepicker";
import ResponsiveTable from "./ResponsiveTable";
import ticketService from "../services/ticketService";
import tasksService from "../services/TasksService";
import organizationalUnitService from "../services/OrganizationalUnitService";
import userPositionService from "../services/UserPositionService";
import ProfileImage from "./ProfileImage";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import Select from 'react-select';
import Spinner from "./Spinner";

const Tickets = () => {
  const { user } = useContext(AuthContext);

  // State
  const [tickets, setTickets] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newTicket, setNewTicket] = useState({
    ticketTypeId: "",
    title: "",
    description: "",
    priority: "Normal",
    slaDueDateOverride: null
  });
  const [processData, setProcessData] = useState({
    status: "Resolved",
    resolutionNotes: ""
  });

  // Dynamic Assignment States
  const [isDynamicAssignmentWorkflow, setIsDynamicAssignmentWorkflow] = useState(false);
  const [orgUnits, setOrgUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [approvalType, setApprovalType] = useState("AllUsers");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [unitUsers, setUnitUsers] = useState([]);
  const [loadingDynamicData, setLoadingDynamicData] = useState(false);
  const [hasCheckedDynamicAssignmentWorkflow, setHasCheckedDynamicAssignmentWorkflow] = useState(false);
  const isManager = Number(user?.userType) === 2;
  const isDarkTheme = (document.documentElement.getAttribute('data-theme') || 'light') === 'dark';

  const userSelectListTextStyles = {
    option: (base, state) => ({
      ...base,
      color: '#495057'
    }),
    menuList: (base) => ({
      ...base,
      color: '#495057'
    })
  };

  const normalizeNamePart = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value).trim();
    if (!text) return "";
    const lowered = text.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    return text;
  };

  const getUserDisplayName = (userRecord) => {
    const firstName = normalizeNamePart(userRecord?.firstName);
    const lastName = normalizeNamePart(userRecord?.lastName);
    const userName = normalizeNamePart(userRecord?.username || userRecord?.userName);

    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    if (firstName) return firstName;
    if (lastName) return lastName;
    if (userName) return userName;
    return `User #${userRecord?.id ?? userRecord?.userId ?? "N/A"}`;
  };

  // Load tickets on component mount
  useEffect(() => {
    console.log("[Tickets] Component mounted, user:", user);
    if (user?.userId) {
      fetchTickets();
      fetchTicketTypes();
    } else {
      console.warn("[Tickets] User not available yet");
    }
  }, [user]);

  // Fetch tickets
  const fetchTickets = async () => {
    try {
      setLoading(true);
      // Fetch only current user's tickets (like LeaveRequest)
      const data = await ticketService.getUserTickets(user.userId);
      setTickets(data);
    } catch (error) {
      toast.error("Failed to load tickets");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ticket types
  const fetchTicketTypes = async () => {
    try {
      const data = await ticketService.getTicketTypes();
      setTicketTypes(data);
    } catch (error) {
      toast.error("Failed to load ticket types");
      console.error(error);
    }
  };

  // Check if Ticket process type has dynamic assignment on first step for current user
  const checkDynamicAssignmentWorkflow = async () => {
    try {
      setLoadingDynamicData(true);
      setHasCheckedDynamicAssignmentWorkflow(false);
      console.log("[Tickets Debug] Checking dynamic assignment for user:", user?.userId);
      const hasDynamicAssignment = await tasksService.checkProcessTypeHasDynamicAssignment("Ticket", user.userId);
      console.log("[Tickets Debug] Dynamic assignment result:", hasDynamicAssignment);
      setIsDynamicAssignmentWorkflow(hasDynamicAssignment);
      
      if (hasDynamicAssignment) {
        await loadOrganizationalUnits();
      }
    } catch (error) {
      console.error("Error checking dynamic assignment workflow:", error);
      setIsDynamicAssignmentWorkflow(false);
      toast.error("Could not verify workflow mode. Please try again.");
    } finally {
      setLoadingDynamicData(false);
      setHasCheckedDynamicAssignmentWorkflow(true);
    }
  };

  // Load organizational units
  const loadOrganizationalUnits = async () => {
    try {
      const units = await organizationalUnitService.getOrganizationChart();
      setOrgUnits(units || []);
    } catch (error) {
      console.error("Failed to load organizational units:", error);
      toast.error("Failed to load organizational units");
    }
  };

  // Handle approval type change and load users if needed
  useEffect(() => {
    const loadUsersIfNeeded = async () => {
      if (selectedUnit && approvalType === "SpecificUsers") {
        try {
          console.log(`[Tickets] Loading users for unit ${selectedUnit}`);
          const positions = await userPositionService.getUserPositionsByUnitId(selectedUnit);
          const uniqueUsersMap = new Map();

          (positions || []).forEach((positionItem) => {
            const userId = Number(positionItem?.userId ?? 0);
            if (!userId) return;

            uniqueUsersMap.set(userId, {
              value: userId,
              label: normalizeNamePart(positionItem?.userFullName) || `User #${userId}`
            });
          });

          const formattedUsers = Array.from(uniqueUsersMap.values())
            .sort((left, right) => left.label.localeCompare(right.label));

          setUnitUsers(formattedUsers || []);
          console.log(`[Tickets] Loaded ${formattedUsers.length} users`);
        } catch (error) {
          console.error("Failed to load unit users:", error);
          toast.error("Failed to load users for selected unit");
        }
      }
    };
    
    loadUsersIfNeeded();
  }, [selectedUnit, approvalType]);

  // Helper function to render organizational units hierarchically
  const renderUnitOption = (unit, level = 0) => {
    const hasUsers = unit.assignedUsers && unit.assignedUsers.length > 0;
    return (
      <React.Fragment key={unit.id}>
        <option value={unit.id} disabled={!hasUsers}>
          {unit.name}
          {unit.assignedUsers && unit.assignedUsers.length > 0 && ` (${unit.assignedUsers.length} users)`}
        </option>
        {unit.children && unit.children.length > 0 && unit.children.map(child => renderUnitOption(child, level + 1))}
      </React.Fragment>
    );
  };

  // Handle organizational unit selection
  const handleUnitChange = async (unitId) => {
    console.log(`[Tickets] Unit selected: ${unitId}`);
    setSelectedUnit(unitId);
    setSelectedUsers([]); // Clear selected users when unit changes
    setUnitUsers([]); // Clear unit users to trigger reload
    // useEffect will handle loading users if approvalType is SpecificUsers
  };

  // Handle user selection toggle
  const handleUserToggle = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle showing create modal - check dynamic assignment when modal opens
  const handleShowCreateModal = async () => {
    setNewTicket({
      ticketTypeId: "",
      title: "",
      description: "",
      priority: "Normal",
      slaDueDateOverride: null
    });
    setSelectedUnit(null);
    setSelectedUsers([]);
    setApprovalType("AllUsers");
    setShowCreateModal(true);
    // Check dynamic assignment workflow now that modal is opening
    await checkDynamicAssignmentWorkflow();
  };

  // Handle create ticket
  const handleCreateTicket = async () => {
    if (loadingDynamicData) {
      toast.error("Please wait until workflow mode finishes loading");
      return;
    }

    if (!hasCheckedDynamicAssignmentWorkflow) {
      toast.error("Workflow check incomplete. Please try again.");
      return;
    }

    if (!newTicket.ticketTypeId || !newTicket.title) {
      toast.error("Please fill in required fields");
      return;
    }

    // Validate dynamic assignment fields only if workflow requires it
    if (isDynamicAssignmentWorkflow) {
      if (!selectedUnit) {
        toast.error("Please select an organizational unit");
        return;
      }

      if (approvalType === "SpecificUsers" && selectedUsers.length === 0) {
        toast.error("Please select at least one user");
        return;
      }
    }

    try {
      setSubmitting(true);
      
      // Build ticket creation request
      const ticketData = {
        ticketTypeId: newTicket.ticketTypeId,
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority || "Normal"
      };

      if (isManager && newTicket.slaDueDateOverride) {
        ticketData.slaDueDateOverride = new Date(newTicket.slaDueDateOverride).toISOString();
      }

      // Only include dynamic assignment fields if workflow supports it
      if (isDynamicAssignmentWorkflow) {
        ticketData.approvalType = approvalType; // "AllUsers" or "SpecificUsers"
        ticketData.unitId = Number(selectedUnit);
        // Only include specific user IDs if "SpecificUsers" is selected
        if (approvalType === "SpecificUsers") {
          ticketData.userIds = selectedUsers.map(user => Number(user.value));
        } else {
          // "AllUsers" - empty list means all users in unit
          ticketData.userIds = [];
        }
      }

      const createdTicket = await ticketService.createTicket(user.userId, ticketData);
      // Dynamic assignment approvers are already set in the backend during InitiateWorkflowForEntityAsync
      // No extra assignTaskToUnit call needed here - that would create a redundant "Assigned" activity.
      
      toast.success("Ticket created successfully");
      setShowCreateModal(false);
      setNewTicket({
        ticketTypeId: "",
        title: "",
        description: "",
        priority: "Normal",
        slaDueDateOverride: null
      });
      setSelectedUnit(null);
      setSelectedUsers([]);
      setApprovalType("AllUsers");
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create ticket");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle process ticket
  const handleProcessTicket = async () => {
    if (!processData.status) {
      toast.error("Please select a status");
      return;
    }

    try {
      setSubmitting(true);
      await ticketService.processTicket(selectedTicket.id, processData);
      toast.success("Ticket processed successfully");
      setShowProcessModal(false);
      setProcessData({
        status: "Resolved",
        resolutionNotes: ""
      });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to process ticket");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete ticket
  const handleDeleteTicket = async (id) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      try {
        await ticketService.deleteTicket(id);
        toast.success("Ticket deleted successfully");
        fetchTickets();
      } catch (error) {
        toast.error("Failed to delete ticket");
        console.error(error);
      }
    }
  };

  // Filter tickets based on search query
  const filteredTickets = tickets.filter(ticket =>
    ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.id.toString().includes(searchQuery) ||
    ticket.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get badge color for status
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'inprogress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'secondary';
      case 'cancelled':
        return 'danger';
      default:
        return 'info';
    }
  };

  // Get badge color for priority
  const getPriorityBadgeColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low':
        return 'info';
      case 'normal':
        return 'secondary';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Table columns configuration
  const columns = [
    {
      label: "ID",
      key: "id",
      sortable: true,
      render: (value) => <span className="fw-bold">#{value}</span>
    },
    {
      label: "Title",
      key: "title",
      sortable: true,
      render: (value) => <span className="text-truncate">{value}</span>
    },
    {
      label: "Type",
      key: "ticketTypeName",
      sortable: true,
      render: (value) => <span>{value}</span>
    },
    {
      label: "Priority",
      key: "priority",
      sortable: true,
      render: (value) => (
        <Badge bg={getPriorityBadgeColor(value)}>
          {value}
        </Badge>
      )
    },
    {
      label: "Status",
      key: "status",
      sortable: true,
      render: (value) => (
        <Badge bg={getStatusBadgeColor(value)}>
          {value}
        </Badge>
      )
    },
    {
      label: "User",
      key: "userFullName",
      sortable: true,
      render: (value) => <span>{value}</span>
    },
    {
      label: "Created",
      key: "createdAt",
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      label: "Actions",
      key: "actions",
      render: (_, row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setSelectedTicket(row);
              setShowDetailsModal(true);
            }}
            title="View Details"
          >
            <FaEye />
          </Button>
          {row.status?.toLowerCase() !== 'closed' && row.status?.toLowerCase() !== 'resolved' && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => {
                setSelectedTicket(row);
                setProcessData({
                  status: "Resolved",
                  resolutionNotes: ""
                });
                setShowProcessModal(true);
              }}
              title="Process Ticket"
            >
              <FaCheckCircle />
            </Button>
          )}
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteTicket(row.id)}
            title="Delete"
          >
            <FaTimes />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col md={6}>
          <Button
            variant="primary"
            onClick={handleShowCreateModal}
          >
            <FaPlus /> Create Ticket
          </Button>
        </Col>
        <Col md={6}>
        </Col>
      </Row>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={12}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <FormControl
              placeholder="Search by ID, Title, or Status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Tickets Table */}
      <Row>
        <Col md={12}>
          <ResponsiveTable
            columns={columns}
            data={filteredTickets}
            keyField="id"
            striped
            hover
            bordered
          />
        </Col>
      </Row>

      {/* Create Ticket Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ticket Type *</Form.Label>
              <Form.Select
                value={newTicket.ticketTypeId}
                onChange={(e) => setNewTicket({ ...newTicket, ticketTypeId: e.target.value })}
                className="form-control"
              >
                <option value="">Select a Ticket Type</option>
                {ticketTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter ticket title"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                maxLength={200}
              />
              <small className="text-muted">{newTicket.title.length}/200</small>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter detailed description"
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                maxLength={2000}
              />
              <small className="text-muted">{newTicket.description.length}/2000</small>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={newTicket.priority}
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </Form.Select>
            </Form.Group>

            {isManager && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SLA Due Date Override</Form.Label>
                    <DatePicker
                      selected={newTicket.slaDueDateOverride}
                      onChange={(date) => setNewTicket({ ...newTicket, slaDueDateOverride: date })}
                      showTimeSelect
                      timeIntervals={15}
                      dateFormat="MMM d, yyyy h:mm aa"
                      minDate={new Date()}
                      className="form-control"
                      placeholderText="Use workflow default SLA"
                    />
                    <Form.Text className="text-muted">
                      Optional. If selected, this due date overrides SLA deadline.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Dynamic Assignment Section - Regular form fields */}
            {isDynamicAssignmentWorkflow && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Select Organizational Unit {selectedUnit && unitUsers.length > 0 && `(${unitUsers.length} users)`} *</Form.Label>
                  {loadingDynamicData ? (
                    <div className="text-center">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Form.Select
                      value={selectedUnit || ""}
                      onChange={(e) => handleUnitChange(e.target.value)}
                    >
                      <option value="">Select a unit...</option>
                      {orgUnits.map(unit => renderUnitOption(unit))}
                    </Form.Select>
                  )}
                </Form.Group>

                {selectedUnit && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Approval Type</Form.Label>
                      <div>
                        <Form.Check
                          type="radio"
                          id="allUsers"
                          label="All Users in Unit"
                          name="approvalType"
                          value="AllUsers"
                          checked={approvalType === "AllUsers"}
                          onChange={(e) => {
                            setApprovalType(e.target.value);
                            setSelectedUsers([]);
                            setUnitUsers([]);
                          }}
                        />
                        <Form.Check
                          type="radio"
                          id="specificUsers"
                          label="Specific Users"
                          name="approvalType"
                          value="SpecificUsers"
                          checked={approvalType === "SpecificUsers"}
                          onChange={(e) => {
                            setApprovalType(e.target.value);
                            setSelectedUsers([]);
                            setUnitUsers([]); // Clear to force reload
                          }}
                        />
                      </div>
                    </Form.Group>

                    {approvalType === "SpecificUsers" && (
                      <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="mb-0">
                            Select Users {selectedUsers.length > 0 && `(${selectedUsers.length} selected)`} *
                          </Form.Label>
                          <Button 
                            variant="link" 
                            size="sm" 
                            onClick={() => setSelectedUsers(unitUsers)}
                            className="p-0"
                          >
                            Select All
                          </Button>
                        </div>
                        {loadingDynamicData ? (
                          <div className="text-center">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : unitUsers.length > 0 ? (
                          <Select
                            isMulti
                            options={unitUsers}
                            value={selectedUsers}
                            onChange={(selected) => setSelectedUsers(selected)}
                            placeholder="Select users who will receive the ticket..."
                            styles={userSelectListTextStyles}
                          />
                        ) : (
                          <p className="text-muted">No users in selected unit</p>
                        )}
                      </Form.Group>
                    )}
                  </>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateModal(false);
            setSelectedUnit(null);
            setSelectedUsers([]);
            setApprovalType("AllUsers");
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTicket}
            disabled={submitting || loadingDynamicData || !hasCheckedDynamicAssignmentWorkflow}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Ticket #{selectedTicket.id} Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Title:</strong>
                <p>{selectedTicket.title}</p>
              </Col>
              <Col md={6}>
                <strong>Type:</strong>
                <p>{selectedTicket.ticketTypeName}</p>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Priority:</strong>
                <p>
                  <Badge bg={getPriorityBadgeColor(selectedTicket.priority)}>
                    {selectedTicket.priority}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <strong>Status:</strong>
                <p>
                  <Badge bg={getStatusBadgeColor(selectedTicket.status)}>
                    {selectedTicket.status}
                  </Badge>
                </p>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Created By:</strong>
                <p>{selectedTicket.userFullName}</p>
              </Col>
              <Col md={6}>
                <strong>Created At:</strong>
                <p>{new Date(selectedTicket.createdAt).toLocaleString()}</p>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={12}>
                <strong>Description:</strong>
                <p className="border-start border-3 ps-3">{selectedTicket.description}</p>
              </Col>
            </Row>
            {selectedTicket.resolutionNotes && (
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Resolution Notes:</strong>
                  <p className="border-start border-3 ps-3 border-success">
                    {selectedTicket.resolutionNotes}
                  </p>
                </Col>
              </Row>
            )}
            {selectedTicket.processedByUserFullName && (
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Processed By:</strong>
                  <p>{selectedTicket.processedByUserFullName} on {new Date(selectedTicket.processedAt).toLocaleString()}</p>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Process Ticket Modal */}
      {selectedTicket && (
        <Modal show={showProcessModal} onHide={() => setShowProcessModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Process Ticket #{selectedTicket.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={processData.status}
                  onChange={(e) => setProcessData({ ...processData, status: e.target.value })}
                >
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                  <option value="Cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Resolution Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Enter resolution notes..."
                  value={processData.resolutionNotes}
                  onChange={(e) => setProcessData({ ...processData, resolutionNotes: e.target.value })}
                  maxLength={1000}
                />
                <small className="text-muted">{processData.resolutionNotes.length}/1000</small>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProcessModal(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleProcessTicket}
              disabled={submitting}
            >
              {submitting ? <Spinner animation="border" size="sm" /> : "Process"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default Tickets;
